import type {MutableRefObject} from 'react';
import React, {useMemo, useRef, useState} from 'react';

import {useDirection} from '../hooks/useDirection';
import {
  normalizeMoveableObject,
  useObjectPlacement,
} from './hooks/useObjectPlacement';
import {EmojiObject} from './ObjectTypes/Emoji/EmojiObject';
import {LinkObject} from './ObjectTypes/Link/LinkObject';
import {PollObject} from './ObjectTypes/Poll/PollObject';
import {StickerObject} from './ObjectTypes/Sticker/StickerObject';
import {TextObject} from './ObjectTypes/Text/TextObject';
import type {BaseObject, CanvasSize} from './types';

interface MoveableObjectProps<T extends BaseObject> {
  object: T;
  selectedObject: T | null;
  objectPlacementEnabled: boolean;
  setSelectedObject: (object: T | null) => void;
  handleMouseDown: (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    object: T,
    containerRef: MutableRefObject<HTMLDivElement>
  ) => void;
  handleDoubleClick?: (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    object: T
  ) => void;
  canvasSize: CanvasSize;
  isPreviewMode: boolean;
  dynamicTextPlaceholder?: string;
}

// object types which include localized text content, and as such will need dynamic positioning to ensure they do not overflow in any locales
const LOCALIZED_OBJECT_TYPES = ['text'];

export const MoveableObject = <T extends BaseObject>({
  object,
  selectedObject,
  handleMouseDown,
  handleDoubleClick,
  canvasSize,
  setSelectedObject,
  objectPlacementEnabled,
  isPreviewMode,
  dynamicTextPlaceholder,
}: MoveableObjectProps<T>) => {
  const isLocalizedObject = LOCALIZED_OBJECT_TYPES.includes(object.type);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isContentLoaded, setIsContentLoaded] = useState(!isLocalizedObject);

  const handleContentReady = () => {
    setIsContentLoaded(true);
  };

  const normalizedObject = useMemo(
    () => normalizeMoveableObject({object, enabled: objectPlacementEnabled}),
    [object, objectPlacementEnabled]
  );

  const {position, scale} = useObjectPlacement({
    object: normalizedObject,
    containerRef,
    canvasSize,
    enabled: objectPlacementEnabled && isContentLoaded && isLocalizedObject,
  });

  const direction = useDirection(containerRef);

  const onMouseDown = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (!containerRef.current) {
      return;
    }
    const mutableRef: MutableRefObject<HTMLDivElement> = {
      ...containerRef,
      current: containerRef.current,
    };

    handleMouseDown(e, object, mutableRef);
  };

  const renderObject = () => {
    function narrowObjectType<K extends BaseObject['type']>(
      obj: T,
      type: K
    ): obj is Extract<T, {type: K}> {
      return obj.type === type;
    }

    const objectComponents: Record<BaseObject['type'], React.ReactNode | null> =
      {
        text: narrowObjectType(object, 'text') ? (
          <TextObject
            object={object}
            canvasSize={canvasSize}
            handleDoubleClick={handleDoubleClick}
            objectPlacementEnabled={objectPlacementEnabled}
            isPreviewMode={isPreviewMode}
            placeholder={dynamicTextPlaceholder}
            onRenderReady={handleContentReady}
            objectScale={scale}
          />
        ) : null,
        sticker: narrowObjectType(object, 'sticker') ? (
          <StickerObject object={object} canvasSize={canvasSize} />
        ) : null,
        poll: narrowObjectType(object, 'poll') ? (
          <PollObject object={object} setSelectedObject={setSelectedObject} />
        ) : null,
        stepLink: narrowObjectType(object, 'stepLink') ? (
          <LinkObject object={object} setSelectedObject={setSelectedObject} />
        ) : null,
        emoji: narrowObjectType(object, 'emoji') ? (
          <EmojiObject object={object} canvasSize={canvasSize} />
        ) : null,
      };

    return objectComponents[object.type] ?? null;
  };

  return (
    <div
      dir='auto'
      className='editor-object'
      ref={containerRef}
      onMouseDown={onMouseDown}
      style={{
        position: 'absolute',
        transform: `scale(${scale}) rotate(${normalizedObject.styles.rotate}deg)`,
        transformOrigin: objectPlacementEnabled ? 'top left' : 'center',
        ...position,
        zIndex: object === selectedObject ? 99 : 1,
        textAlign: normalizedObject.styles.textAlign,
        direction,
      }}
    >
      {renderObject()}
    </div>
  );
};
